/* eslint-disable */

import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { logOutUser, generateToastError } from '../../../utils';
import { queryKeys } from '../queryKeys';

// Twilio Video API hooks

const httpStatuses = { unauthorized: 401, error: 500 };

/**
 * Hook to generate Twilio Video access token
 * 
 * @param {string} meetingId - Meeting ID
 * @param {string} userToken - User authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 * @returns {object} React Query result
 */
export const useQueryTwilioVideoToken = (meetingId, userToken, navigate, toast) => {
  const queryKey = [queryKeys.twilioVideoToken, meetingId];

  const queryFn = () =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/twilio-video/token/${meetingId}`, {}, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken && !!meetingId,
    retry: 2,
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    onError: (error) => {
      console.error('Failed to get Twilio Video token:', error);
      if (error.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
      } else {
        generateToastError(error, toast);
      }
    },
    onSuccess: (data) => {
      
    },
  });
};

/**
 * Hook to get room information
 * 
 * @param {string} roomName - Room name
 * @param {string} userToken - User authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 * @returns {object} React Query result
 */
export const useQueryTwilioVideoRoom = (roomName, userToken, navigate, toast) => {
  const queryKey = [queryKeys.twilioVideoRoom, roomName];

  const queryFn = () =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/twilio-video/room/${roomName}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken && !!roomName,
    retry: 2,
    cacheTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000, // 1 minute
    onError: (error) => {
      console.error('Failed to get room info:', error);
      if (error.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
      } else {
        generateToastError(error, toast);
      }
    },
  });
};

/**
 * Hook to end video room
 * 
 * @param {function} toast - Toast notification function
 * @returns {object} Mutation result
 */
export const useMutationEndTwilioVideoRoom = (toast) => {
  return useMutation(
    async ({ roomName, meetingId }) => {
      return axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/twilio-video/room/${roomName}/end`,
        { meetingId },
        {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).token : ''}`,
            'Content-Type': 'application/json'
          },
        }
      );
    },
    {
      onSuccess: (data) => {
        console.log('Room ended successfully:', data.data);
        toast.success('Meeting ended successfully');
      },
      onError: (error) => {
        console.error('Failed to end room:', error);
        if (error.response?.status === httpStatuses.unauthorized) {
          logOutUser();
        } else {
          generateToastError(error, toast);
        }
      },
    }
  );
};

/**
 * Hook to get Twilio Video health status
 * 
 * @param {string} userToken - User authentication token
 * @param {function} navigate - Navigation function
 * @param {function} toast - Toast notification function
 * @returns {object} React Query result
 */
export const useQueryTwilioVideoHealth = (userToken, navigate, toast) => {
  const queryKey = [queryKeys.twilioVideoHealth];

  const queryFn = () =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/twilio-video/health`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken,
    retry: 2,
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    onError: (error) => {
      console.error('Twilio Video health check failed:', error);
      if (error.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
      } else {
        generateToastError(error, toast);
      }
    },
  });
};
